import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Shell } from '../../components/layout/Shell';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { Bell, CheckCircle, EnvelopeSimple } from '@phosphor-icons/react';

const TYPE_LABEL = {
  invite: 'INVITED',
  interviewed: 'INTERVIEWED',
  offered: 'OFFERED',
  hire: 'HIRED',
  reject: 'CLOSED',
  info: 'INFO',
};

const CandidateNotifications = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get('/notifications/mine')
      .then((r) => setItems(r.data || []))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const markAllRead = async () => {
    try {
      await api.post('/notifications/read-all');
      setItems((xs) => xs.map((x) => ({ ...x, is_read: true })));
      toast.success('All notifications cleared');
    } catch {
      toast.error('Could not update');
    }
  };

  const markOne = async (id) => {
    try {
      await api.post('/notifications/read', { id });
      setItems((xs) => xs.map((x) => x.id === id ? { ...x, is_read: true } : x));
    } catch { /* ignore */ }
  };

  const unread = items.filter((x) => !x.is_read).length;

  return (
    <Shell>
      <div className="max-w-[900px] mx-auto px-6 py-10">
        <div className="flex items-end justify-between mb-8 gap-3 flex-wrap">
          <div>
            <div className="label-eyebrow text-primary mb-2">YOUR INBOX</div>
            <h1 className="font-display font-black text-4xl tracking-tighter">
              {unread > 0 ? `${unread} new update${unread === 1 ? '' : 's'}.` : 'You are all caught up.'}
            </h1>
          </div>
          {unread > 0 && (
            <Button variant="outline" className="rounded-none" onClick={markAllRead} data-testid="mark-all-read">
              Mark all read
            </Button>
          )}
        </div>

        {loading ? (
          <div className="mono text-sm text-muted-foreground">Loading...</div>
        ) : items.length === 0 ? (
          <div className="border border-border bg-white p-10 text-center" data-testid="notifications-empty">
            <Bell size={32} weight="duotone" className="mx-auto text-muted-foreground mb-2" />
            <div className="label-eyebrow text-muted-foreground mb-2">NO NOTIFICATIONS YET</div>
            <p className="text-sm text-muted-foreground mb-4">
              When an employer invites you or updates your application status, it'll show up here.
            </p>
            <Link to="/candidate/jobs" className="label-eyebrow text-primary">BROWSE JOBS →</Link>
          </div>
        ) : (
          <ul className="border border-border bg-white" data-testid="notifications-list">
            {items.map((n) => (
              <li
                key={n.id}
                className={`p-5 border-b border-border flex items-start gap-4 ${n.is_read ? 'opacity-60' : 'bg-secondary/30'}`}
                data-testid={`notification-${n.id}`}
              >
                <div className="mt-1">
                  {n.is_read
                    ? <CheckCircle size={18} weight="duotone" className="text-muted-foreground" />
                    : <EnvelopeSimple size={18} weight="duotone" className="text-primary" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="label-eyebrow text-primary">{TYPE_LABEL[n.type] || n.type.toUpperCase()}</span>
                    <span className="mono text-xs text-muted-foreground">{new Date(n.created_at).toLocaleString()}</span>
                  </div>
                  <div className="font-display font-black text-lg mb-1">{n.title}</div>
                  {n.body && <p className="text-sm text-muted-foreground">{n.body}</p>}
                  {n.link && (
                    <Link
                      to={n.link}
                      onClick={() => markOne(n.id)}
                      className="label-eyebrow text-primary mt-2 inline-block"
                      data-testid={`notification-link-${n.id}`}
                    >
                      OPEN →
                    </Link>
                  )}
                </div>
                {!n.is_read && (
                  <button
                    onClick={() => markOne(n.id)}
                    className="label-eyebrow text-xs text-muted-foreground hover:text-foreground"
                    data-testid={`notification-mark-${n.id}`}
                  >
                    MARK READ
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Shell>
  );
};

export default CandidateNotifications;
